import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import type {
  BenchmarkDefinition,
  CriterionDefinition,
  CriterionDetails,
  EvalGridRow,
  QuestionEvalResult,
  RubricResult,
} from "./types";

import {
  CriterionDetailsDialog,
  QuestionScoreOutcomeChip,
  QuestionSourceLinks,
  ScoreChip,
} from "./ScoutEvalCommon";
import {
  buildBenchmarksUrl,
  compactPanelSx,
  formatDate,
  formatEnumLabel,
  formatPercent,
  formatPoints,
  formatQuestionName,
  formatStatusLabel,
  getAggregationRoleChipColor,
  getBenchmarkDefinition,
  getLatestEvalRows,
  getProgressValue,
  getQuestionDefinition,
  getQuestionMaxPoints,
  getQuestionPoints,
  getScoreProgressColor,
  getWeightedScore,
  panelSx,
  shouldShowAggregationRole,
  titleFromKey,
} from "./scoutEvalUtils";

function LatestEvalSummary({ evalRows }: { evalRows: EvalGridRow[] }) {
  const latestRows = getLatestEvalRows(evalRows);
  const totalScore = getWeightedScore(latestRows);
  const totalCost = latestRows.reduce((total, row) => total + row.estimatedCostUsd, 0);
  const theme = useTheme();
  const latestEval = latestRows[0];
  const hasOnlySucceededRows = latestRows.every((row) => row.status === "SUCCEEDED");

  if (!latestEval) {
    return (
      <Alert severity="info" variant="outlined">
        No Scout eval results have been recorded yet.
      </Alert>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 280px) 1fr" },
        gap: 3,
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="overline" color="text.secondary">
          Latest eval results
        </Typography>
        <Typography variant="h4" lineHeight={1}>
          {hasOnlySucceededRows ? formatPercent(totalScore) : formatStatusLabel(latestEval.status)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {latestEval.jobId ? `Job ${latestEval.jobId}` : `Eval ${latestEval.id}`} across{" "}
          {latestRows.length} benchmark{latestRows.length === 1 ? "" : "s"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {latestEval.model ?? "Unknown model"} |{" "}
          {formatDate(latestEval.completedAt ?? latestEval.createdAt)} | ${totalCost.toFixed(2)}
        </Typography>
      </Stack>
      <Stack spacing={1.5} justifyContent="center">
        {latestRows.map((row) => (
          <Box key={row.id}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2">{row.benchmarkName}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  {formatPercent(row.score)}
                </Typography>
              </Stack>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={getProgressValue(row.score)}
              color={getScoreProgressColor(row.score)}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: theme.palette.grey[100],
              }}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function EvalsGrid({ evalRows }: { evalRows: EvalGridRow[] }) {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) {
      return evalRows;
    }

    return evalRows.filter((row) => {
      return [
        row.id,
        row.jobId ?? "",
        row.benchmarkName,
        row.model ?? "",
        row.status,
        formatDate(row.completedAt ?? row.createdAt),
        formatPercent(row.score),
      ].some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [evalRows, searchText]);

  const columns = useMemo<CustomGridColDef<EvalGridRow>[]>(
    () => [
      {
        field: "id",
        headerName: "Eval ID",
        width: 330,
        valueGetter(_, row) {
          return row.id;
        },
      },
      {
        field: "benchmarkName",
        headerName: "Benchmark",
        flex: 1,
        minWidth: 260,
        renderCell(params) {
          return <Typography variant="body2">{params.row.benchmarkName}</Typography>;
        },
      },
      {
        field: "score",
        headerName: "Score",
        width: 130,
        renderCell(params) {
          return <ScoreChip score={params.row.score} />;
        },
      },
      {
        field: "model",
        headerName: "Model",
        width: 190,
        valueGetter(_, row) {
          return row.model ?? "Unknown";
        },
      },
      {
        field: "status",
        headerName: "Run Status",
        width: 130,
        valueGetter(_, row) {
          return formatStatusLabel(row.status);
        },
      },
      {
        field: "createdAt",
        headerName: "Date",
        width: 130,
        valueGetter(_, row) {
          return formatDate(row.completedAt ?? row.createdAt);
        },
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <LatestEvalSummary evalRows={evalRows} />
      <Paper elevation={0} sx={panelSx}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <TextField
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search evals"
              size="small"
              sx={{ width: { xs: "100%", sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {filteredRows.length} result{filteredRows.length === 1 ? "" : "s"}
            </Typography>
          </Stack>
          <Box sx={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                sorting: {
                  sortModel: [{ field: "createdAt", sort: "desc" }],
                },
              }}
              onRowClick={({ row }) => {
                navigate(buildBenchmarksUrl({ view: "evals", evalResultId: row.id }));
              }}
              getRowLink={(row) => buildBenchmarksUrl({ view: "evals", evalResultId: row.id })}
              noRowsMessage="No eval results match this search."
            />
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}

function QuestionScoreBarChart({ questionResults }: { questionResults: QuestionEvalResult[] }) {
  const theme = useTheme();

  return (
    <Paper elevation={0} sx={panelSx}>
      <Stack spacing={1.25}>
        <Typography variant="subtitle2">Question scores</Typography>
        {questionResults.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No question results were recorded for this eval.
          </Alert>
        ) : (
          questionResults.map((questionResult) => (
            <Box
              key={questionResult.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 320px) 1fr auto" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Typography variant="body2">
                {formatQuestionName(questionResult.questionKey)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressValue(questionResult.score)}
                color={getScoreProgressColor(questionResult.score)}
                sx={{
                  height: 10,
                  borderRadius: 1,
                  bgcolor: theme.palette.grey[100],
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                <QuestionScoreOutcomeChip
                  status={questionResult.status}
                  score={questionResult.score}
                />
                <Typography variant="body2" fontWeight={600} minWidth={56} textAlign="right">
                  {formatPercent(questionResult.score)}
                </Typography>
              </Stack>
            </Box>
          ))
        )}
      </Stack>
    </Paper>
  );
}

function getCriterionDetailsFromResult({
  rubricResult,
  criterionDefinition,
}: {
  rubricResult: RubricResult;
  criterionDefinition: CriterionDefinition | null;
}): CriterionDetails {
  const details: CriterionDetails = {
    criterionKey: rubricResult.criterionKey,
    category: rubricResult.category,
    scorerType: rubricResult.scorerType,
    scoreAggregationRole: rubricResult.scoreAggregationRole,
    maxPoints: rubricResult.maxPoints,
    instructions: criterionDefinition?.instructions ?? null,
    requirements: criterionDefinition?.requirements ?? null,
    result: {
      passed: rubricResult.passed,
      pointsAwarded: rubricResult.pointsAwarded,
      explanation: rubricResult.explanation,
      evidence: rubricResult.evidence,
    },
  };

  if (criterionDefinition) {
    details.enabled = criterionDefinition.enabled;
  }

  return details;
}

function RubricResultCard({
  rubricResult,
  criterionDefinition,
}: {
  rubricResult: RubricResult;
  criterionDefinition: CriterionDefinition | null;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const criterionDetails = getCriterionDetailsFromResult({ rubricResult, criterionDefinition });

  return (
    <>
      <Paper variant="outlined" sx={compactPanelSx}>
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={700}>
              {titleFromKey(rubricResult.criterionKey)}
            </Typography>
            <Chip
              label={`${formatPoints(rubricResult.pointsAwarded)} / ${formatPoints(
                rubricResult.maxPoints
              )} pts`}
              color={rubricResult.passed ? "success" : "error"}
              size="small"
              variant="outlined"
            />
            <Chip
              label={rubricResult.passed ? "Pass" : "Fail"}
              color={rubricResult.passed ? "success" : "error"}
              size="small"
              variant="outlined"
            />
            {shouldShowAggregationRole(rubricResult.scoreAggregationRole) ? (
              <Chip
                label={formatEnumLabel(rubricResult.scoreAggregationRole)}
                color={getAggregationRoleChipColor(rubricResult.scoreAggregationRole)}
                size="small"
                variant="outlined"
              />
            ) : null}
            <Chip
              label={formatEnumLabel(rubricResult.scorerType)}
              color="default"
              size="small"
              variant="outlined"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatEnumLabel(rubricResult.category)}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setDetailsOpen(true)}
            sx={{ alignSelf: "flex-start" }}
          >
            View details
          </Button>
        </Stack>
      </Paper>
      <CriterionDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        criterion={criterionDetails}
      />
    </>
  );
}

function EvalResultDetail({
  evalResult,
  benchmarks,
}: {
  evalResult: EvalGridRow;
  benchmarks: BenchmarkDefinition[];
}) {
  const benchmark = getBenchmarkDefinition(benchmarks, evalResult.benchmarkKey);

  return (
    <Stack spacing={2}>
      <Button
        component={Link}
        to={buildBenchmarksUrl({ view: "evals" })}
        startIcon={<ArrowBackIcon />}
        sx={{ alignSelf: "flex-start" }}
      >
        Evals
      </Button>
      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto auto auto" },
          gap: 2,
          alignItems: "center",
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="h6">
            Eval {evalResult.id} | {evalResult.benchmarkName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {evalResult.questionResults.length} evaluated question
            {evalResult.questionResults.length === 1 ? "" : "s"}
          </Typography>
        </Stack>
        <ScoreChip score={evalResult.score} />
        <Typography variant="body2">{evalResult.model ?? "Unknown model"}</Typography>
        <Typography variant="body2">
          {formatDate(evalResult.completedAt ?? evalResult.createdAt)}
        </Typography>
      </Paper>
      <QuestionScoreBarChart questionResults={evalResult.questionResults} />
      <Stack spacing={2}>
        {evalResult.questionResults.map((questionResult, questionIndex) => {
          const question = benchmark
            ? getQuestionDefinition(benchmark, questionResult.questionKey)
            : null;

          return (
            <Paper key={questionResult.id} variant="outlined" sx={panelSx}>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {questionIndex + 1}. {formatQuestionName(questionResult.questionKey)}
                    </Typography>
                    {benchmark ? (
                      <MuiLink
                        href={buildBenchmarksUrl({
                          view: "benchmarks",
                          benchmarkKey: benchmark.key,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                      >
                        Question definition
                      </MuiLink>
                    ) : null}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" fontWeight={700}>
                      {formatPoints(getQuestionPoints(questionResult))} /{" "}
                      {formatPoints(getQuestionMaxPoints(questionResult))}
                    </Typography>
                    {questionResult.scoutConversationId ? (
                      <Button
                        href={`/staff/observe/scout-conversation/${questionResult.scoutConversationId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                      >
                        View conversation
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Prompt
                  </Typography>
                  <Typography variant="body2">{questionResult.prompt}</Typography>
                </Box>
                <QuestionSourceLinks sourceLinks={question?.sourceLinks ?? []} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Expected answer
                  </Typography>
                  <Typography variant="body2">
                    {question?.expectedAnswer ?? questionResult.expectedAnswerSnapshot}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Scout response sample
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 3,
                      overflow: "hidden",
                    }}
                  >
                    {questionResult.answerSummary}
                  </Typography>
                </Box>
                <Divider />
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Criteria results</Typography>
                  {questionResult.rubricResults.length === 0 ? (
                    <Alert severity="info" variant="outlined">
                      No criteria results were recorded for this question.
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {questionResult.rubricResults.map((rubricResult) => (
                        <RubricResultCard
                          key={rubricResult.id}
                          rubricResult={rubricResult}
                          criterionDefinition={
                            question?.criteria.find(
                              (criterion) => criterion.criterionKey === rubricResult.criterionKey
                            ) ?? null
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}

export function EvalsTab({
  evalRows,
  benchmarks,
  selectedEvalId,
}: {
  evalRows: EvalGridRow[];
  benchmarks: BenchmarkDefinition[];
  selectedEvalId: string | null;
}) {
  const selectedEvalResult = evalRows.find((row) => row.id === selectedEvalId) ?? null;

  return selectedEvalResult ? (
    <EvalResultDetail evalResult={selectedEvalResult} benchmarks={benchmarks} />
  ) : (
    <EvalsGrid evalRows={evalRows} />
  );
}
